// Step 5 of the client-side X12 pipeline (852 variant): walks one
// transaction's flat body segments (from step 2's envelope tree) into the
// loop structure a human reader thinks in — header, Name loops, LIN
// item-activity loops (with their nested-Name sub-loop), and the summary.
// Only ever runs against a transaction whose ST01 is "852"; anything else is
// left alone so it can still be shown, read-only, by the UI. Mirrors
// parser850.ts's pattern one-for-one, but against 852's own loop shape —
// parser850.ts itself is never touched by this file.

import type {EdiSegment} from './tokenizer';
import type {EdiEnvelope, EdiGroup, EdiTransaction} from './envelope';

export interface Header852 {
  bct?: EdiSegment;
  refs: EdiSegment[];
  dtm: EdiSegment[];
  per?: EdiSegment;
  fob?: EdiSegment;
  pwk: EdiSegment[];
  msg: EdiSegment[];
}

export interface NameLoop852 {
  n1: EdiSegment;
  n2?: EdiSegment;
  n3?: EdiSegment;
  n4?: EdiSegment;
  n5?: EdiSegment;
  n6?: EdiSegment;
  n9: EdiSegment[];
  refs: EdiSegment[];
  per?: EdiSegment;
}

export interface ItemNameSubLoop852 {
  n1: EdiSegment;
  n2?: EdiSegment;
  n3?: EdiSegment;
  n4?: EdiSegment;
}

export interface ItemActivityLoop852 {
  lin: EdiSegment;
  ctp: EdiSegment[];
  refs: EdiSegment[];
  per?: EdiSegment;
  sdq: EdiSegment[];
  qty: EdiSegment[];
  dtm: EdiSegment[];
  pid: EdiSegment[];
  subNameLoops: ItemNameSubLoop852[];
}

export interface Summary852 {
  ctt?: EdiSegment;
  amt: EdiSegment[];
}

export interface Parsed852 {
  header: Header852;
  nameLoops: NameLoop852[];
  items: ItemActivityLoop852[];
  summary: Summary852;
}

function attachHeaderSegment(header: Header852, s: EdiSegment): void {
  switch (s.id) {
    case 'REF':
      header.refs.push(s);
      break;
    case 'DTM':
      header.dtm.push(s);
      break;
    case 'PER':
      header.per = s;
      break;
    case 'FOB':
      header.fob = s;
      break;
    case 'PWK':
      header.pwk.push(s);
      break;
    case 'MSG':
      header.msg.push(s);
      break;
    default:
      break;
  }
}

function attachNameLoopSegment(loop: NameLoop852, s: EdiSegment): void {
  switch (s.id) {
    case 'N2':
      loop.n2 = s;
      break;
    case 'N3':
      loop.n3 = s;
      break;
    case 'N4':
      loop.n4 = s;
      break;
    case 'N5':
      loop.n5 = s;
      break;
    case 'N6':
      loop.n6 = s;
      break;
    case 'N9':
      loop.n9.push(s);
      break;
    case 'REF':
      loop.refs.push(s);
      break;
    case 'PER':
      loop.per = s;
      break;
    default:
      break;
  }
}

function attachItemNameSubLoopSegment(
  loop: ItemNameSubLoop852,
  s: EdiSegment
): void {
  switch (s.id) {
    case 'N2':
      loop.n2 = s;
      break;
    case 'N3':
      loop.n3 = s;
      break;
    case 'N4':
      loop.n4 = s;
      break;
    default:
      break;
  }
}

function attachItemSegment(item: ItemActivityLoop852, s: EdiSegment): void {
  switch (s.id) {
    case 'CTP':
      item.ctp.push(s);
      break;
    case 'REF':
      item.refs.push(s);
      break;
    case 'PER':
      item.per = s;
      break;
    case 'SDQ':
      item.sdq.push(s);
      break;
    case 'QTY':
      item.qty.push(s);
      break;
    case 'DTM':
      item.dtm.push(s);
      break;
    case 'PID':
      item.pid.push(s);
      break;
    default:
      break;
  }
}

function emptyHeader(): Header852 {
  return {refs: [], dtm: [], pwk: [], msg: []};
}

function emptyItem(lin: EdiSegment): ItemActivityLoop852 {
  return {
    lin,
    ctp: [],
    refs: [],
    sdq: [],
    qty: [],
    dtm: [],
    pid: [],
    subNameLoops: [],
  };
}

type Phase = 'header' | 'name' | 'item' | 'summary';
type ItemSubPhase = 'main' | 'name';

/**
 * Walks one 852 transaction's flat body segments (ST/SE excluded) into its
 * loop structure. Never throws — a segment that doesn't fit anywhere
 * sensible in the current loop is simply not attached, rather than the walk
 * aborting; the validator is what reports on a document that's missing or
 * misordered.
 */
export function parse852Segments(segments: EdiSegment[]): Parsed852 {
  const header = emptyHeader();
  const nameLoops: NameLoop852[] = [];
  const items: ItemActivityLoop852[] = [];
  const summary: Summary852 = {amt: []};

  let phase: Phase = 'header';
  let itemSubPhase: ItemSubPhase = 'main';
  let currentNameLoop: NameLoop852 | undefined;
  let currentItem: ItemActivityLoop852 | undefined;
  let currentItemNameSubLoop: ItemNameSubLoop852 | undefined;

  for (const s of segments) {
    switch (s.id) {
      case 'BCT':
        header.bct = s;
        break;

      case 'N1':
        if (phase === 'item' && currentItem) {
          itemSubPhase = 'name';
          currentItemNameSubLoop = {n1: s};
          currentItem.subNameLoops.push(currentItemNameSubLoop);
        } else {
          phase = 'name';
          currentNameLoop = {n1: s, n9: [], refs: []};
          nameLoops.push(currentNameLoop);
        }
        break;

      case 'N2':
      case 'N3':
      case 'N4':
      case 'N5':
      case 'N6':
      case 'N9':
        if (itemSubPhase === 'name' && currentItemNameSubLoop) {
          attachItemNameSubLoopSegment(currentItemNameSubLoop, s);
        } else if (phase === 'name' && currentNameLoop) {
          attachNameLoopSegment(currentNameLoop, s);
        }
        break;

      case 'LIN':
        phase = 'item';
        itemSubPhase = 'main';
        currentItemNameSubLoop = undefined;
        currentItem = emptyItem(s);
        items.push(currentItem);
        break;

      case 'CTT':
        phase = 'summary';
        summary.ctt = s;
        currentItem = undefined;
        currentNameLoop = undefined;
        currentItemNameSubLoop = undefined;
        break;

      case 'AMT':
        summary.amt.push(s);
        break;

      default:
        if (phase === 'header') {
          attachHeaderSegment(header, s);
        } else if (phase === 'name' && currentNameLoop) {
          attachNameLoopSegment(currentNameLoop, s);
        } else if (phase === 'item' && currentItem) {
          attachItemSegment(currentItem, s);
        }
        break;
    }
  }

  return {header, nameLoops, items, summary};
}

export interface EdiTransactionWithParsed852 extends EdiTransaction {
  /** Left undefined for a transaction whose ST01 isn't "852" — it keeps only
   * its raw segment list, for read-only display. */
  parsed852?: Parsed852;
}

export interface EdiGroupWithParsed852 extends Omit<EdiGroup, 'transactions'> {
  transactions: EdiTransactionWithParsed852[];
}

export interface EdiEnvelopeWithParsed852 extends Omit<EdiEnvelope, 'groups'> {
  groups: EdiGroupWithParsed852[];
}

/**
 * Parses every 852 transaction in an envelope tree (step 2's output),
 * attaching `parsed852` to each. A non-852 transaction is passed through
 * unchanged, `parsed852` left undefined.
 */
export function attachParsed852(
  envelope: EdiEnvelope
): EdiEnvelopeWithParsed852 {
  return {
    ...envelope,
    groups: envelope.groups.map(group => ({
      ...group,
      transactions: group.transactions.map(transaction => ({
        ...transaction,
        parsed852:
          transaction.transactionSetCode === '852'
            ? parse852Segments(transaction.segments)
            : undefined,
      })),
    })),
  };
}
